/**
 * Shape helpers for the rejections contact-admin's API layer produces.
 *
 * `api._handleResponse` rejects a PLAIN OBJECT for every non-2xx response —
 * `{ code, message, error, errorCode, isJson, httpStatus, url }` — never an `Error`. Anything that
 * treats a rejection as an `Error` therefore discards both the status and the server's own copy.
 *
 * LSP-19423: that is how ~170 staging error events over eight days carried no message but
 * `Error: [object Object]`, which hid the fact that nearly all of them were an ordinary 404 for an
 * entity the caller had already been told did not exist. The status was in the rejection the whole
 * time; only the stringification threw it away.
 */

/** HTTP statuses that mean "the entity is gone", as opposed to "the request failed". */
const ENTITY_GONE_STATUSES = [404, 410];

const LOWEST_HTTP_STATUS = 100;
const HIGHEST_HTTP_STATUS = 599;

/**
 * The HTTP status an API rejection carries, or `undefined` when it carries none.
 *
 * Deliberately range-gated. `code` is not exclusively an HTTP field: a `DOMException` (an aborted
 * fetch, for instance) carries `code = 20` and is NOT `instanceof Error` in browsers, so it reaches
 * the same non-Error paths as an API rejection. Without the range check it would be reported as
 * "HTTP 20" — a status that never existed.
 *
 * Field priority matches what the API layer actually sets: `httpStatus` is written on every
 * `_handleResponse` rejection, while `code` can be overwritten by a server-supplied `code`.
 */
export function getApiErrorStatus(error: unknown): number | undefined {
	if (!error || typeof error !== "object") return undefined;

	const shape = error as {
		httpStatus?: unknown;
		code?: unknown;
		status?: unknown;
		response?: { status?: unknown };
	};

	// `status` is "error" (a string) on this API's rejections and a number on axios-shaped ones, so
	// every candidate is filtered on type as well as range rather than on presence.
	const candidates = [shape.httpStatus, shape.code, shape.status, shape.response?.status];
	return candidates.find(
		(candidate): candidate is number =>
			typeof candidate === "number" &&
			Number.isInteger(candidate) &&
			candidate >= LOWEST_HTTP_STATUS &&
			candidate <= HIGHEST_HTTP_STATUS
	);
}

/**
 * True when a rejection says the entity no longer exists, rather than that the attempt to read it
 * failed. The distinction decides whether a caller should retry: there is no fresher state to
 * recover for a deleted entity, so retrying only re-requests a resource that will keep 404ing.
 */
export function isEntityGoneError(error: unknown): boolean {
	const status = getApiErrorStatus(error);
	return status !== undefined && ENTITY_GONE_STATUSES.includes(status);
}

/**
 * A readable one-line description of any thrown value — `Error` or not.
 *
 * `String(value)` is what produced `[object Object]`; this reads the fields the API layer actually
 * populates instead. The rejection's `url` is deliberately NOT included: it can carry a claim token
 * in its path, which `api._handleResponse` scrubs before logging (see `scrubClaimToken`).
 */
export function describeThrownValue(error: unknown): string {
	if (typeof error === "string") return error;
	if (!error || typeof error !== "object") return String(error);

	const shape = error as { message?: unknown; error?: unknown; errorCode?: unknown; name?: unknown };
	const status = getApiErrorStatus(error);
	const text = [shape.message, shape.error, shape.errorCode, shape.name].find(
		(candidate): candidate is string => typeof candidate === "string" && candidate.trim().length > 0
	);

	const parts = [status === undefined ? null : `HTTP ${status}`, text ?? null].filter((part): part is string => part !== null);
	if (parts.length > 0) return parts.join(": ");

	try {
		const serialized = JSON.stringify(error);
		// "{}" is no more useful than "[object Object]", so it keeps falling through rather than
		// replacing one uninformative message with another.
		if (serialized && serialized !== "{}") return serialized;
	} catch {
		// Circular or otherwise non-serializable — fall through to String().
	}

	return String(error);
}
